import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import api from '../../services/api';
import ProjectModal from '../../components/ProjectModal';
import TaskModal from '../../components/TaskModal';
import ConfirmModal from '../../components/ConfirmModal';

const STATUS_LABELS = {
  todo: 'To Do',
  in_progress: 'In Progress',
  testing: 'Testing',
  client_review: 'Client Review',
  completed: 'Completed'
};

const STATUS_COLORS = {
  todo: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  testing: 'bg-purple-100 text-purple-700',
  client_review: 'bg-orange-100 text-orange-700',
  completed: 'bg-green-100 text-green-700'
};

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700'
};

const ProjectOverviewPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [projectFilterSearch, setProjectFilterSearch] = useState('');
  const [projectFilterOpen, setProjectFilterOpen] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  const projectFilterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (projectFilterRef.current && !projectFilterRef.current.contains(e.target)) {
        setProjectFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTasks = () => {
    api.get('/admin/project-overview')
      .then(res => setTasks((res.data.tasks || []).sort((a, b) => {
        const clientCmp = (a.Client?.name || '').localeCompare(b.Client?.name || '');
        if (clientCmp !== 0) return clientCmp;
        return (a.Project?.name || '').localeCompare(b.Project?.name || '');
      })))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTasks();
    api.post('/clients/list').then(r => setClients(r.data)).catch(() => {});
    api.get('/users', { params: { limit: 1000 } }).then(r => setUsers(r.data.users || r.data)).catch(() => {});
    api.post('/projects/list', { limit: 1000 }).then(r => setAllProjects(r.data.projects || r.data)).catch(() => {});
    api.get('/users', { params: { limit: 1000 } }).then(r => setAllUsers(r.data.users || r.data)).catch(() => {});
  }, []);

  const projects = [...new Map(tasks.filter(t => t.Project).map(t => [t.Project.id, t.Project])).values()]
    .sort((a, b) => a.name.localeCompare(b.name));

  // Build a lookup map: project_id -> project object
  const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));

  const filtered = tasks.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      t.title.toLowerCase().includes(q) ||
      (t.Client?.name || '').toLowerCase().includes(q) ||
      (t.Project?.name || '').toLowerCase().includes(q) ||
      (t.Users || []).some(u => u.name.toLowerCase().includes(q));
    const matchStatus = !filterStatus || t.status === filterStatus;
    const matchProject = !filterProject || String(t.Project?.id) === filterProject;
    return matchSearch && matchStatus && matchProject;
  });

  const exportCSV = () => {
    const headers = ['Client', 'Project', 'Task', 'Priority', 'Status', 'Assigned To', 'Due Date', 'Created', 'Last Update'];
    const rows = filtered.map(t => [
      t.Client?.name || '',
      t.Project?.name || '',
      t.title,
      t.priority || '',
      STATUS_LABELS[t.status] || t.status,
      (t.Users || []).map(u => u.name).join('; '),
      fmt(t.due_date || ''),
      fmt(t.created_at),
      fmt(t.updated_at)
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-overview-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleEditTask = (task) => {
    setCurrentTask(task);
    setShowTaskModal(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      await api.put(`/admin/tasks/${currentTask.id}`, taskData);
      setShowTaskModal(false);
      fetchTasks();
    } catch (err) {
      console.error('Error saving task:', err);
      alert('Failed to save task.');
    }
  };

  const handleDeleteTask = (task) => {
    setCurrentTask(task);
    setShowDeleteTaskModal(true);
  };

  const handleConfirmDeleteTask = async () => {
    try {
      await api.delete(`/admin/tasks/${currentTask.id}`);
      setShowDeleteTaskModal(false);
      fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task.');
    }
  };

  const handleEditProject = async (project) => {
    try {
      const response = await api.get(`/projects/${project.id}`);
      setCurrentProject(response.data);
    } catch {
      setCurrentProject(project);
    }
    setShowProjectModal(true);
  };

  const handleSaveProject = async (projectData) => {
    try {
      await api.put(`/projects/${currentProject.id}`, projectData);
      setShowProjectModal(false);
      fetchTasks();
    } catch (err) {
      console.error('Error saving project:', err);
      alert('Failed to save project. Please try again.');
    }
  };

  const handleDeleteProject = (project) => {
    setCurrentProject(project);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/projects/${currentProject.id}`);
      setShowDeleteModal(false);
      fetchTasks();
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project. Please try again.');
    }
  };

  const fmt = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  if (loading) return <div className="p-6 text-center py-16">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Project Overview</h1>
        <button
          onClick={exportCSV}
          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center gap-2"
        >
          ⬇ Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Task, client, project, assignee..."
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-64"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All Status</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="relative" ref={projectFilterRef}>
          <label className="block text-xs font-medium text-gray-600 mb-1">Project</label>
          <input
            type="text"
            value={projectFilterSearch}
            onChange={e => { setProjectFilterSearch(e.target.value); setProjectFilterOpen(true); }}
            onFocus={() => setProjectFilterOpen(true)}
            placeholder={filterProject ? projects.find(p => String(p.id) === filterProject)?.name : 'All Projects'}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-48"
            autoComplete="off"
          />
          {filterProject && (
            <button
              type="button"
              onClick={() => { setFilterProject(''); setProjectFilterSearch(''); }}
              className="absolute right-2 top-8 text-gray-400 hover:text-red-500 text-xs"
            >
              &times;
            </button>
          )}
          {projectFilterOpen && (
            <div className="absolute z-20 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
              <button
                type="button"
                onClick={() => { setFilterProject(''); setProjectFilterSearch(''); setProjectFilterOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-b"
              >
                All Projects
              </button>
              {projects
                .filter(p => p.name.toLowerCase().includes(projectFilterSearch.toLowerCase()))
                .map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setFilterProject(String(p.id)); setProjectFilterSearch(p.name); setProjectFilterOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${String(p.id) === filterProject ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                  >
                    {p.name}
                  </button>
                ))
              }
              {projects.filter(p => p.name.toLowerCase().includes(projectFilterSearch.toLowerCase())).length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400">No projects found</div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => { setSearch(''); setFilterStatus(''); setFilterProject(''); setProjectFilterSearch(''); }}
          className="px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
        >
          Clear
        </button>
        <span className="ml-auto text-sm text-gray-500 self-center">{filtered.length} tasks</span>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Client', 'Project', 'Task', 'Priority', 'Status', 'Assigned To', 'Due Date', 'Created', 'Last Update', ''].map((h, i) => (
                  <th key={h || i} className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${h === '' ? 'sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-gray-400">No tasks found</td>
                </tr>
              ) : filtered.map(task => (
                <tr
                  key={task.id}
                  onClick={() => navigate(`/admin/tasks/${task.id}`)}
                  className={`cursor-pointer hover:bg-gray-50 ${task.priority === 'high' && task.status !== 'completed' ? 'border-l-4 border-red-500' : ''}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                    {task.Client?.name || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                    {task.Project?.name || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="font-medium text-gray-900 truncate">{task.title}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[task.priority] || 'bg-gray-100 text-gray-600'}`}>
                      {task.priority?.charAt(0).toUpperCase() + task.priority?.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[task.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABELS[task.status] || task.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {task.Users && task.Users.length > 0
                        ? task.Users.map(u => (
                          <span key={u.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">
                            {u.name}
                          </span>
                        ))
                        : <span className="text-gray-400 text-xs">Unassigned</span>
                      }
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{task.due_date ? fmt(task.due_date) : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{fmt(task.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{fmt(task.updated_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap sticky right-0 bg-white shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditTask(task)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit Task"
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteTask(task)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete Task"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showTaskModal && (
        <TaskModal
          task={currentTask}
          onClose={() => setShowTaskModal(false)}
          onSave={handleSaveTask}
          users={allUsers}
          clients={clients}
          projects={allProjects}
        />
      )}
      {showDeleteTaskModal && (
        <ConfirmModal
          title="Delete Task"
          message={`Are you sure you want to delete "${currentTask?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDeleteTask}
          onCancel={() => setShowDeleteTaskModal(false)}
        />
      )}
      {showProjectModal && (
        <ProjectModal
          project={currentProject}
          onClose={() => setShowProjectModal(false)}
          onSave={handleSaveProject}
          clients={clients}
          users={users}
        />
      )}
      {showDeleteModal && (
        <ConfirmModal
          title="Delete Project"
          message={`Are you sure you want to delete "${currentProject?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default ProjectOverviewPage;
